<template>
  <div class="range-variant">
    <div class="range-variant__values">
      <div
        v-for="(item, index) in items"
        :key="index"
        class="range-variant__value"
        :class="getColor(index)"
      >
        {{ item.percentage }}
      </div>
    </div>
    <div class="range-variant__container">
      <div class="range-variant__slider-track"></div>
      <input
        v-for="(item, index) in items"
        :key="index"
        type="range"
        min="0"
        max="100"
        :value="item.count"
        :style="{
          display:
            items.length > 1 && index === items.length - 1 ? 'none' : 'block',
          zIndex: getZindex(index),
        }"
        @input="slide($event, index)"
        :disabled="disabled"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, defineEmits, defineProps, PropType } from "vue";
import { debounce } from "lodash";

// Определите типы для ваших входных данных
interface Variant {
  count: number;
  percentage: number;
}

// Определите props с использованием синтаксиса defineProps
const props = defineProps({
  variants: {
    type: Array as PropType<Variant[]>,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

// Data и Reactivity
const items = ref<Variant[]>(props.variants); // используйте ref для реактивных данных
const minGap = 0;

const emit = defineEmits(["change-variants"]);

// Следите за изменениями в props.variants
watch(
  () => props.variants,
  (newValue) => {
    items.value = newValue;
  },
  { immediate: true }
);

// Методы
const slide = (e: Event, index: number) => {
  const target = e.target as HTMLInputElement;
  const previousValue = target.previousSibling as HTMLInputElement;
  const nextValue = target.nextSibling as HTMLInputElement;

  if (
    previousValue &&
    parseInt(target.value) - parseInt(previousValue.value) <= minGap
  ) {
    target.value = (parseInt(previousValue.value) + minGap).toString();
  } else if (
    nextValue &&
    parseInt(nextValue.value) - parseInt(target.value) <= minGap
  ) {
    target.value = (parseInt(nextValue.value) - minGap).toString();
  }

  if (items.value[index]) {
    items.value[index].count = parseInt(target.value);
    if (items.value[index + 1]) {
      items.value[index + 1].percentage =
        items.value[index + 1].count - items.value[index].count;
    }
    if (index > 0) {
      items.value[index].percentage =
        items.value[index].count - items.value[index - 1].count;
    } else if (items.value.length > 1) {
      items.value[index].percentage = items.value[index].count;
    }
  }

  onChangeDebounced();
};

// Debounced onChange - используйте onMounted для инициализации
let onChangeDebounced = () => {
  emit("change-variants", items.value);
};
onMounted(() => {
  onChangeDebounced = debounce(onChangeDebounced, 400);
});

const getColor = (index: number): string => {
  const colors = ["green", "yellow", "red", "blue", "violet", "gray"];
  return colors[index % colors.length] ?? colors[0];
};

const getZindex = (index: number) => {
  const itemsCount = items.value.length;
  if (
    itemsCount > 2 &&
    index === itemsCount - 3 &&
    items.value[itemsCount - 1].percentage === 0
  ) {
    return 2;
  }
  return 1;
};
</script>

<style lang="scss" scoped>
.range-variant {
  position: relative;
  &__values {
    position: relative;
    display: flex;
    align-items: center;
    margin: auto;
    padding: 10px 0;
    border-radius: 5px;
    text-align: center;
    font-weight: 500;
    font-size: 25px;
    color: #ffffff;
  }

  &__value {
    flex-grow: 1;
  }

  &__container {
    position: relative;
    width: 100%;
    height: 14px;
  }

  &__slider-track {
    width: 100%;
    height: 4px;
    position: absolute;
    margin: auto;
    top: 0;
    bottom: 0;
    border-radius: 90px;
    background: #e8e8e8;
  }
  input[type="range"] {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    width: 100%;
    outline: none;
    position: absolute;
    margin: auto;
    top: 0;
    bottom: 0;
    background-color: transparent;
    pointer-events: none;
    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: 14px;
      height: 14px;
      flex-shrink: 0;
      background-color: #1b212d;
      border: 1px solid #fff;
      border-radius: 50%;
      cursor: pointer;
      margin-top: -5px;
      pointer-events: auto;
      // &:last-child {
      //   background-color: red;
      // }
    }
  }
  input[type="range"]::-webkit-slider-runnable-track {
    -webkit-appearance: none;
    height: 4px;
  }
  input[type="range"]::-moz-range-track {
    -moz-appearance: none;
    height: 4px;
  }
  input[type="range"]::-ms-track {
    appearance: none;
    height: 4px;
  }
  input[type="range"]::-moz-range-thumb {
    -webkit-appearance: none;
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    background-color: #1b212d;
    border: 1px solid #fff;
    border-radius: 50%;
    cursor: pointer;
    margin-top: -5px;
    pointer-events: auto;
  }
  input[type="range"]::-ms-thumb {
    appearance: none;
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    background-color: #1b212d;
    border: 1px solid #fff;
    border-radius: 50%;
    cursor: pointer;
    pointer-events: auto;
  }
  input[type="range"]:active::-webkit-slider-thumb {
    background-color: #ffffff;
    border: 1px solid #1b212d;
  }

  .green {
    color: #6db90c;
  }
  .yellow {
    color: #f5c828;
  }
  .red {
    color: #f66141;
  }
  .blue {
    color: #3697f1;
  }
  .violet {
    color: #9747ff;
  }
  .gray {
    color: #979797;
  }
}
</style>
